import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './galeriafotos.module.scss';
import type { ImageItem, PhotoGroup, GaleriaFotosProps } from './GaleriaFotos.types';
import Icona from 'components/corporatiu/ui/Icona';
import Imatge from 'components/corporatiu/Imatge';
import { getDataUTC } from '@portal-internet/core';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';

const MIDA_THUMBNAIL = 'Thumbnail';
const MIDA_FULLSCREEN = 'fullscreen';
const MIDA_MAX659 = 'max659';
const SLIDESHOW_INTERVAL = 4000;

const buildPhotoGroups = (imatges: ImageItem[]): PhotoGroup[] => {
    const map: Record<string, PhotoGroup> = {};

    for (const img of imatges) {
        const { group_id, text, mida, peu = '' } = img;
        if (!map[group_id]) {
            map[group_id] = { group_id, thumbnail: '', fullscreen: '', caption: peu };
        }
        if (mida === MIDA_THUMBNAIL) {
            map[group_id].thumbnail = text;
        }
        if (mida === MIDA_FULLSCREEN) {
            map[group_id].fullscreen = text;
        }
        if (mida === MIDA_MAX659 && !map[group_id].fullscreen) {
            map[group_id].fullscreen = text;
        }
        if (peu && !map[group_id].caption) {
            map[group_id].caption = peu;
        }
    }

    return Object.values(map).filter((g) => g.thumbnail || g.fullscreen);
};

export default function GaleriaFotos({ imatges, titol, entradeta, dataPublicacio, estil }: GaleriaFotosProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const swipeStartRef = useRef<{ x: number; y: number } | null>(null);

    const photos = imatges ? buildPhotoGroups(imatges) : [];
    const total = photos.length;

    const goNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % total);
    }, [total]);

    const goPrev = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + total) % total);
    }, [total]);

    const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (event.pointerType !== 'touch') return;
        swipeStartRef.current = { x: event.clientX, y: event.clientY };
    }, []);

    const handlePointerUp = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (event.pointerType !== 'touch' || !swipeStartRef.current) return;

            const deltaX = event.clientX - swipeStartRef.current.x;
            const deltaY = event.clientY - swipeStartRef.current.y;
            swipeStartRef.current = null;

            if (Math.abs(deltaX) < 60 || Math.abs(deltaX) < Math.abs(deltaY)) return;

            if (deltaX < 0) {
                goNext();
            } else {
                goPrev();
            }
        },
        [goNext, goPrev]
    );

    const clearSwipeState = useCallback(() => {
        swipeStartRef.current = null;
    }, []);

    // Slideshow auto-advance
    useEffect(() => {
        if (!isPlaying || total <= 1) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            return;
        }

        timeoutRef.current = setTimeout(() => {
            goNext();
        }, SLIDESHOW_INTERVAL);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isPlaying, currentIndex, goNext, total]);

    useEffect(() => {
        if (!isPlaying && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [isPlaying]);

    if (!total) return null;

    const photo = photos[currentIndex];

    const stylesGaleria = clsx(styles.galeria, !__isEmpty(estil) && styles[estil as string]);

    return (
        <>
            <div className={stylesGaleria}>
                {/* Header info */}
                {(titol || dataPublicacio || entradeta) && (
                    <div className={styles.header}>
                        {titol && <h1 className={styles.titol}>{titol}</h1>}
                        {dataPublicacio && (
                            <p className={styles.data}>
                                <time dateTime={getDataUTC(dataPublicacio)} className={styles.dataPub}>
                                    {dataPublicacio.split(' ')[0]}
                                </time>
                            </p>
                        )}
                        {entradeta && <p className={styles.entradeta}>{entradeta}</p>}
                    </div>
                )}

                {/* Main photo */}
                <div
                    className={styles.photoWrapper}
                    onPointerDown={handlePointerDown}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={clearSwipeState}
                    onPointerLeave={clearSwipeState}
                >
                    <Imatge
                        className={styles.photo}
                        src={photo.fullscreen || photo.thumbnail}
                        alt={photo.caption || `Foto ${currentIndex + 1} de ${total}`}
                        layout='fill'
                        objectFit='contain'
                    />
                    {isPlaying && total > 1 && (
                        <div className={styles.progressBar} aria-hidden={true}>
                            <span key={photo.group_id} className={styles.progressFill} />
                        </div>
                    )}
                </div>
                {/* Bottom overlay */}
                <div className={styles.bottomBar}>
                    <span className={styles.counter} aria-live='polite'>
                        {currentIndex + 1}/{total}
                    </span>
                    {photo.caption && <span className={styles.caption}>{photo.caption}</span>}
                    <div className={styles.controls}>
                        <button type='button' className={styles.controlBtn} onClick={goPrev} aria-label='Foto anterior'>
                            <Icona icona='fletxa_esquerra' isBlanc={true} ariaHidden={true} />
                        </button>
                        <button
                            type='button'
                            className={styles.controlBtn}
                            onClick={() => setIsPlaying((p) => !p)}
                            aria-label={isPlaying ? 'Aturar presentació' : 'Iniciar presentació'}
                            title={isPlaying ? 'Aturar presentació' : 'Iniciar presentació'}
                        >
                            <Icona icona={isPlaying ? 'pause_ple' : 'play'} isBlanc={true} ariaHidden={true} />
                        </button>
                        <button type='button' className={styles.controlBtn} onClick={goNext} aria-label='Foto següent'>
                            <Icona icona='fletxa_dreta' isBlanc={true} ariaHidden={true} />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
